import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { 
  fetchConversationsData, 
  startNewConversation, 
  markMessagesAsRead,
  archiveConversation as archiveConversationService
} from '@/services/conversationService';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js'; // Importer le type

export interface Conversation {
  id: string;
  participant1_id: string;
  participant2_id: string;
  last_message: string | null;
  last_message_time: string | null;
  status: 'active' | 'archived' | 'blocked';
  partner: {
    id: string;
    name: string;
    avatar?: string | null;
    isOnline?: boolean;
    isVerified?: boolean;
    type: 'escort' | 'client';
    city?: string;
  } | null;
  unreadCount: number;
}

// Interface pour le retour RPC
interface HiddenIdResult {
  conversation_id: string;
}

// Interfaces simplifiées pour les types de tables (à ajuster selon le schéma réel si nécessaire)
interface DbMessage { 
  id: string;
  conversation_id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  created_at: string;
  // ... autres champs ... 
}

interface DbConversation {
  id: string;
  participant1_id: string;
  participant2_id: string;
  status: 'active' | 'archived' | 'blocked';
  // ... autres champs ...
}

interface DbHiddenConversation {
  id: string;
  user_id: string;
  conversation_id: string;
  created_at: string;
}

interface UseConversationsReturn {
  conversations: Conversation[];
  isLoading: boolean;
  startConversation: (partnerId: string) => Promise<Conversation | null>;
  markConversationAsRead: (conversationId: string) => Promise<void>;
  archiveConversation: (conversationId: string) => Promise<void>;
  fetchConversations: () => Promise<void>;
  totalUnreadCount: number;
}

export const useConversations = (): UseConversationsReturn => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const { user } = useAuth();
  const { toast } = useToast();

  const updateTotalUnread = (convs: Conversation[]) => {
    const total = convs.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
    setTotalUnreadCount(total);
  };

  const _fetchConversations = useCallback(async () => {
    if (!user) {
      setConversations([]);
      setTotalUnreadCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let hiddenConversationIds: string[] = [];

    try {
      // Attempt to fetch hidden conversation IDs first
      try {
        // console.log("[useConversations] Fetching hidden conversation IDs...");
        // const { data: hiddenIdsData, error: hiddenIdsError } = await supabase.rpc('get_hidden_conversation_ids');
        
        // if (hiddenIdsError) {
        //   console.error("Error fetching hidden conversation IDs:", hiddenIdsError);
        //   // Optionally throw the error or handle it gracefully
        //   // For now, we'll proceed without filtering hidden conversations if this fails
        // } else if (hiddenIdsData) {
        //   console.log("[useConversations] Hidden IDs fetched:", hiddenIdsData);
        //   hiddenConversationIds = hiddenIdsData; // Assuming the RPC returns an array of UUIDs
        // } else {
        //   console.log("[useConversations] No hidden IDs found or RPC returned null.");
        // }
      } catch (error) {
        console.error("Unexpected error fetching hidden conversation IDs:", error);
      }

      // 2. Récupérer les conversations
      const fetchedConversations = await fetchConversationsData(user.id);

      // 3. Filtrer
      const filteredConversations = fetchedConversations.filter(conv => {
          const isBlocked = conv.status === 'blocked';
          const isHidden = hiddenConversationIds.includes(conv.id);
          return !isBlocked && !isHidden;
      });

      // console.log("Fetched conversations:", fetchedConversations.length);
      // console.log("Filtered (non-blocked, non-hidden) conversations:", filteredConversations.length);

      setConversations(filteredConversations);
      updateTotalUnread(filteredConversations);

    } catch (error) {
      // Gestion sûre de l'erreur
      console.error('Error fetching conversations in hook:', error);
      const description = error instanceof Error ? error.message : 'Impossible de charger les conversations';
      toast({
        title: 'Erreur',
        description: description,
        variant: 'destructive',
      });
      setConversations([]);
      setTotalUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // Initial fetch and real-time subscriptions
  useEffect(() => {
    if (user) {
      _fetchConversations();

      // console.log("Setting up Supabase subscriptions for conversations and messages...");
      
      // Subscription for new messages to update conversation list (e.g., last message, unread count)
      const messagesSubscription = supabase
        .channel('public:messages:conversations-hook')
        .on<DbMessage>('postgres_changes', { // Spécifier le type de table ici
          event: 'INSERT', // Écouter seulement les INSERTs ici
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${user.id}` // Attention: nécessite que recipient_id soit en REPLICA IDENTITY FULL ou INDEX
        }, (payload: RealtimePostgresChangesPayload<DbMessage>) => { // Typer le payload
          
          console.log('[useConversations] New message detected via Realtime:', payload);
          const newMessage = payload.new as DbMessage; // Assertion de type prudente ici

          // Vérifier si le message est pour l'utilisateur actuel et n'est pas de lui-même
          // et que newMessage et ses propriétés existent
          if (newMessage && typeof newMessage === 'object' && newMessage.recipient_id && newMessage.sender_id && 
              newMessage.recipient_id === user.id && newMessage.sender_id !== user.id && newMessage.conversation_id) {
              setConversations(prevConvs => {
                  const convIndex = prevConvs.findIndex(c => c.id === newMessage.conversation_id);
                  
                  if (convIndex > -1) {
                      // Conversation trouvée, la mettre à jour
                      const updatedConv = {
                          ...prevConvs[convIndex],
                          // Utiliser les valeurs du nouveau message si elles existent
                          last_message: newMessage.content ?? prevConvs[convIndex].last_message,
                          last_message_time: newMessage.created_at ?? prevConvs[convIndex].last_message_time,
                          unreadCount: (prevConvs[convIndex].unreadCount || 0) + 1
                      };
                      // Créer une nouvelle liste avec la conversation mise à jour en premier
                      const newList = [
                          updatedConv,
                          ...prevConvs.slice(0, convIndex),
                          ...prevConvs.slice(convIndex + 1)
                      ];
                      updateTotalUnread(newList);
                      return newList;
                  } else {
                      // Conversation non trouvée (peut arriver si elle était cachée/archivée ou nouvelle)
                      // On pourrait re-fetcher pour être sûr, ou ignorer pour l'instant
                       console.warn(`[useConversations] Received message for unknown/filtered conversation ${newMessage.conversation_id}`);
                      // Optionnel : déclencher _fetchConversations() si une nouvelle conversation apparaîtrait
                      return prevConvs; 
                  }
              });
          }
          
          // _fetchConversations(); // <-- Ne pas recharger toute la liste ici
        })
        .subscribe((status, err) => {
            if (err) {
                console.error("Error subscribing to messages:", err);
            }
            // console.log("Messages subscription status:", status);
        });

      // Subscription for conversation updates (e.g., status changes)
      const conversationsSubscription = supabase
        .channel('public:conversations:conversations-hook')
        .on<DbConversation>('postgres_changes', { // Spécifier le type de table
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          // Attention: Le filtre sur participants peut être complexe/inefficace.
          // Une approche pourrait être d'écouter toutes les updates et filtrer côté client,
          // ou utiliser une fonction RPC appelée par un trigger pour notifier spécifiquement.
          // filter: `participant1_id=eq.${user.id},participant2_id=eq.${user.id}` // Filter relevant to the user
        }, (payload: RealtimePostgresChangesPayload<DbConversation>) => { // Typer le payload
          console.log('Conversation update detected, updating specific conversation:', payload);
          const updatedConv = payload.new as DbConversation; // Assertion de type
          
          // Vérifier si updatedConv et ses propriétés existent
          if (updatedConv && typeof updatedConv === 'object' && updatedConv.id && updatedConv.status && 
              (updatedConv.participant1_id === user.id || updatedConv.participant2_id === user.id)) {
            setConversations(prevConvs => {
                const newList = prevConvs.map(conv => 
                    conv.id === updatedConv.id ? { ...conv, status: updatedConv.status } : conv
                );
                updateTotalUnread(newList);
                return newList;
            });
          } else {
              console.log('[useConversations] Conversation update received, but not relevant or invalid data.', payload);
          }
          // _fetchConversations(); // Refetch conversations on update
        })
        .subscribe((status, err) => {
            if (err) {
                console.error("Error subscribing to conversations:", err);
            }
            // console.log("Conversations subscription status:", status);
        });

      const hiddenConversationsSubscription = supabase
         .channel('public:hidden_conversations:conversations-hook')
         .on<DbHiddenConversation>('postgres_changes', { // Spécifier le type de table
           event: '*',
           schema: 'public',
           table: 'hidden_conversations',
           filter: `user_id=eq.${user.id}`
         },
         (payload: RealtimePostgresChangesPayload<DbHiddenConversation>) => { // Typer le payload
             console.log('Hidden conversation change -> Filtering conversations list:', payload);
             const { eventType, new: newHidden, old: oldHidden } = payload;
             let needsRefetch = false;
             setConversations(prev => {
                 let newList = prev;
                 if (eventType === 'INSERT' && newHidden) {
                     newList = prev.filter(c => c.id !== newHidden.conversation_id);
                 } else if (eventType === 'DELETE' && oldHidden) {
                     needsRefetch = true; // Need to refetch to potentially get the conversation back
                 } else {
                     needsRefetch = true; // Refetch for updates or other cases
                 }
                 updateTotalUnread(newList);
                 return newList;
             });
             if (needsRefetch) {
                _fetchConversations(); // Refetch if necessary
             }
         })
         .subscribe((status, err) => {
             if (err) {
                 console.error("Error subscribing to hidden_conversations:", err);
             }
         });

      // Cleanup function
      return () => {
        // console.log("Removing Supabase subscriptions...");
        if (messagesSubscription) {
          supabase.removeChannel(messagesSubscription).catch(err => console.error("Error removing messages channel:", err));
        }
        if (conversationsSubscription) {
          supabase.removeChannel(conversationsSubscription).catch(err => console.error("Error removing conversations channel:", err));
        }
        if (hiddenConversationsSubscription) {
          supabase.removeChannel(hiddenConversationsSubscription).catch(err => console.error("Error removing hidden_conversations channel:", err));
        }
      };
    } else {
        // Ensure cleanup if user logs out
        // console.log("User logged out, removing subscriptions if any...");
        supabase.removeAllChannels(); // Clear all channels on logout
        setConversations([]);
        setTotalUnreadCount(0);
        setIsLoading(false);
    }
  }, [user, _fetchConversations]);

  // Fonction pour démarrer une conversation, maintenant utilise le service
  const startConversation = useCallback(async (partnerId: string): Promise<Conversation | null> => {
    if (!user) {
      toast({ title: 'Erreur', description: 'Utilisateur non connecté', variant: 'destructive' });
      return null;
    }

    // console.log(`Attempting to start conversation with ${partnerId}`);
    try {
      const newOrExistingConv = await startNewConversation(user.id, partnerId);
      if (newOrExistingConv) {
        // console.log("Conversation started/found:", newOrExistingConv.id);
        // Vérifier si la conversation existe déjà dans l'état
        if (!conversations.some(c => c.id === newOrExistingConv.id)) {
            // console.log("Adding new/found conversation to state:", newOrExistingConv);
            setConversations(prev => [newOrExistingConv, ...prev]); // Ajouter en début de liste
        } else {
            // Déplacer la conversation existante au début si nécessaire
            setConversations(prev => {
                 const existingIndex = prev.findIndex(c => c.id === newOrExistingConv.id);
                 if (existingIndex > 0) {
                     const convToMove = prev[existingIndex];
                     return [convToMove, ...prev.slice(0, existingIndex), ...prev.slice(existingIndex + 1)];
                 }
                 return prev;
            });
        }
        updateTotalUnread(conversations.map(c => c.id === newOrExistingConv.id ? newOrExistingConv : c));
        return newOrExistingConv;
      }
    } catch (error) {
       console.error('Error starting conversation in hook:', error);
       const description = error instanceof Error ? error.message : 'Impossible de démarrer la conversation';
       toast({ title: 'Erreur', description, variant: 'destructive' });
    }
    return null;
  }, [user, toast, conversations]); // Ajouter conversations aux dépendances

  // Fonction pour marquer une conversation comme lue
  const markConversationAsRead = useCallback(async (conversationId: string) => {
     if (!user) return;
     console.log(`[useConversations] Marking conversation ${conversationId} as read locally.`);
     let changed = false;
     setConversations(prevConvs => {
         const newList = prevConvs.map(conv => {
            if (conv.id === conversationId && conv.unreadCount > 0) {
                changed = true;
                return { ...conv, unreadCount: 0 };
            }
            return conv;
        });
        if (changed) {
            updateTotalUnread(newList);
        }
        return newList;
     });
     
     if (changed) {
         try {
             console.log(`[useConversations] Calling service to mark messages as read in DB for conversation ${conversationId}.`);
             await markMessagesAsRead(conversationId, user.id);
             console.log(`[useConversations] Service call finished for marking as read.`);
         } catch (error) {
             console.error("Error marking conversation as read in DB:", error);
             // Optionnel: Revert local state ou afficher une erreur?
             // Pour l'instant, on laisse l'état local tel quel pour éviter un "saut" de l'UI
             toast({ title: 'Erreur', description: 'Impossible de marquer les messages comme lus dans la base de données.', variant: 'destructive' });
         }
     }
  }, [user, toast]);

  // Fonction pour archiver une conversation
  const archiveConversation = useCallback(async (conversationId: string) => {
    if (!user) return;
    
    // Correction: Définir originalConversations avant setConversations
    const originalConversations = conversations; 
    const conversationToArchive = originalConversations.find(c => c.id === conversationId);
    let newList = originalConversations;
    if (conversationToArchive) {
        newList = originalConversations.filter(c => c.id !== conversationId);
        setConversations(newList);
        updateTotalUnread(newList); // Mettre à jour total
    } else {
         // Si la conversation n'est pas trouvée, ne rien faire ou logguer une erreur
         console.warn(`[archiveConversation] Conversation ${conversationId} not found in local state.`);
         return; // Ne pas continuer si la conversation n'existe pas localement
    }

    try {
      await archiveConversationService(conversationId, user.id);
      toast({ title: 'Succès', description: 'Conversation archivée.' });
    } catch (error) {
      console.error("Error archiving conversation in hook:", error);
      toast({ title: 'Erreur', description: 'Impossible d\'archiver la conversation', variant: 'destructive' });
      // Revert optimistic update
      setConversations(originalConversations);
      updateTotalUnread(originalConversations); // Restaurer le total
    }
  }, [user, toast, conversations]);

  return { 
      conversations, 
      isLoading, 
      startConversation, 
      markConversationAsRead,
      archiveConversation,
      fetchConversations: _fetchConversations,
      totalUnreadCount
    };
};
